import { useCallback, useMemo } from 'react';
import { DateTime } from 'luxon';
import { RenderIf } from 'react-rainbow-components';
import Button from 'components/Button';
import ButtonIcon from 'components/ButtonIcon';
import Close from 'components/icons/close';
import Gradient from 'components/Gradient';
import Calendar from 'components/icons/calendar';
import useSearchFilters from 'hooks/useSearchFilters';
import Accordion from '../../../../Opportunities/View/accordion';
import {
    FilterHeader,
    Title,
    FiltersContainer,
    FiltersContent,
} from '../../../../Opportunities/View/styled';
import {
    FailedToggleContainer,
    StyledDatePickerFilter,
    StyledFailedToggle,
} from './styled';

export const WEBHOOK_LOG_FILTER_NAMES = ['createdAt', 'failed'];

interface Params {
    onRequestClose?: () => void;
}

const Filters = ({
    onRequestClose = () => {},
}: Params) => {
    const {
        values,
        getValue: getFilterValue,
        setValue: setFilterValue,
        clear: clearFilters,
    } = useSearchFilters({ filterNames: WEBHOOK_LOG_FILTER_NAMES });

    const activeFiltersCount = useMemo(
        () => WEBHOOK_LOG_FILTER_NAMES.reduce(
            (count, key) => (
                getFilterValue(key) !== null
                    ? count + 1
                    : count
            ),
            0,
        ),
        [getFilterValue],
    );

    const createdAtFilterValue = useMemo(
        () => {
            const { createdAt } = values;
            if (!createdAt) return undefined;
            return String(createdAt).split(',').map((strDate) => DateTime.fromISO(strDate).toJSDate());
        },
        [values],
    );

    const handleChangeCreatedAt = useCallback(
        (newFilterValue: Date[]) => setFilterValue(
            'createdAt',
            (
                newFilterValue.length === 0
                    ? null
                    : newFilterValue.map((date) => date.toISOString()).join(',')
            ),
        ),
        [setFilterValue],
    );

    const failedOnly = values.failed === 'true';

    const handleChangeFailed = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            setFilterValue('failed', event.target.checked ? 'true' : null);
        },
        [setFilterValue],
    );

    return (
        <FiltersContainer>
            <FilterHeader>
                <Title>
                    Filters
                    {' '}
                    <RenderIf isTrue={activeFiltersCount > 0}>
                        (
                        {activeFiltersCount}
                        )
                    </RenderIf>
                </Title>
                <div>
                    <Button
                        label="Clear"
                        variant="base"
                        size="small"
                        disabled={!activeFiltersCount}
                        borderRadius="semi-square"
                        onClick={clearFilters}
                    />
                    <ButtonIcon
                        borderRadius="semi-square"
                        size="small"
                        icon={<Close />}
                        onClick={onRequestClose}
                    />
                </div>
            </FilterHeader>
            <FiltersContent>
                <Accordion icon={<Calendar />} label="Created At" name="createdAt">
                    <StyledDatePickerFilter
                        onChange={handleChangeCreatedAt}
                        value={createdAtFilterValue}
                    />
                </Accordion>
                <Accordion label="Status" name="failed">
                    <FailedToggleContainer>
                        <StyledFailedToggle
                            label="Show only failed requests"
                            labelAlignment="right"
                            value={failedOnly}
                            onChange={handleChangeFailed}
                        />
                    </FailedToggleContainer>
                </Accordion>
            </FiltersContent>
            <Gradient position="bottom" />
        </FiltersContainer>
    );
};

export default Filters;
